import { Box, Menu, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { CustomButton } from '../../../../components/mui/CustomButton';
import CustomTypography from '../../../../components/mui/CustomTypography';
import { ReactEditor } from 'slate-react';
import { Transforms } from 'slate';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { CustomTooltips } from '../../../../components';

const ExaminationForm = ({ element, editor, fontSize, examinations, allowEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    examinationId: element.examination?.examinationId || crypto.randomUUID(),
    examinationType: element.examination?.examinationType?.toString() || '',
    witnessName: element.examination?.witnessName || '',
    examinedBy: element.examination?.examinedBy || '',
    examinationStatus: element.examination?.examinationStatus?.toString() || '',
  });

  const handleClick = useCallback((event) => {
    event.stopPropagation();
    if (!allowEdit) return;
    setAnchorEl(event.currentTarget);
    setFormData({
      examinationId: element.examination?.examinationId || crypto.randomUUID(),
      examinationType: element.examination?.examinationType?.toString() || '',
      witnessName: element.examination?.witnessName || '',
      examinedBy: element.examination?.examinedBy || '',
      examinationStatus: element.examination?.examinationStatus?.toString() || '',
    });
  }, [element]);

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      {
        examination: {
          ...formData,
        },
      },
      { at: path },
    );
    onClose();
  };

  const removeExamination = (e) => {
    e.stopPropagation();
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(
      editor,
      { examination: null },
      { at: path },
    );
  };

  return (
    <Box sx={{ position: 'relative' }} contentEditable={false}>
      {element.examination?.examinationId ? (
        <span className='d-flex align-items-center gap-2' style={{
          backgroundColor: '#1900413d',
          padding: '2px 6px',
          borderRadius: '6px',
        }}>
          <CustomTypography
            contentEditable={false}
            size="smallMedium"
            variant="p"
            sx={{
              fontSize: `${Math.max(fontSize, 10)}px !important`,
              color: '#190041 !important',
              textAlign: 'left',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
            onClick={handleClick}
          >
            {`Examination ${element.examination?.examinationStatus == '1' ? 'Started' : element.examination?.examinationStatus == '2' ? 'Paused' : 'Ended'}`}
          </CustomTypography>
          <CustomTooltips title="Remove Examination" id="t-7">
            <HighlightOffRoundedIcon style={{ cursor: 'pointer', opacity: 0.5, height: '18px', width: '18px' }} onClick={removeExamination} />
          </CustomTooltips>
        </span>
      ) : (
        <Tooltip title="Add Examination" placement="top">
          <StickyNote2OutlinedIcon
            sx={{
              width: `${Math.max(fontSize, 10)}px`,
              height: `${Math.max(fontSize, 10)}px`,
              color: '#190041 !important',
              cursor: 'pointer',
            }}
            onClick={handleClick}
          />
        </Tooltip>
      )}

      <Menu
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorEl={anchorEl}
        disableAutoFocusItem
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            maxHeight: '400px',
            width: '300px',
            background: '#fff',
            border: '1px solid #ccc',
            padding: '10px',
            boxShadow: '0px 4px 4px rgb(0 0 0 / 25%)',
            borderRadius: '4px',
          },
        }}
      >
        <form onSubmit={handleSubmit} onKeyDown={(e) => e.stopPropagation()}>
          {examinations
            .filter((field) => field.FieldName !== 'examinationId')
            .map((field) => {
              const { FieldName, DisplayName, Values, Validation } = field;

              const isRequired = Validation?.includes('required');
              const value = formData[FieldName] || '';

              if (!Values || Values.length === 0) {
                return (
                  <div key={FieldName} style={{ paddingBottom: '10px' }}>
                    <label className="form-label">
                      {DisplayName}
                      <span style={{ color: 'red' }}>{isRequired ? ' *' : ''}</span>
                    </label>
                    <TextField
                      id={FieldName}
                      size="small"
                      fullWidth
                      required={isRequired}
                      value={value}
                      onChange={handleChange}
                      disabled={!allowEdit}
                    />
                  </div>
                );
              }

              return (
                <div key={FieldName} style={{ paddingBottom: '10px' }}>
                  <label className="form-label">
                    {DisplayName}
                    <span style={{ color: 'red' }}>{isRequired ? ' *' : ''}</span>
                  </label>
                  <Select
                    fullWidth
                    size="small"
                    value={value}
                    required={isRequired}
                    onChange={handleSelectChange(FieldName)}
                    disabled={!allowEdit}
                  >
                    {Values.map(({ Key, Value }) => (
                      <MenuItem key={Key} value={Key}>
                        {Value}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              );
            })}

          {allowEdit && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CustomButton type="submit" variant="secondary">
              Save
            </CustomButton>
          </div>}
        </form>
      </Menu>
    </Box>
  );
};

export default ExaminationForm;
